import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import AuthLayout from "../../layouts/AuthLayout";
import { authApi } from "../../api/api";

interface ForgotPasswordFormData {
  email: string;
}

interface ResetPasswordFormData {
  token: string;
  email: string;
  password: string;
  password_confirmation: string;
}

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const email = searchParams.get("email");

  const [step, setStep] = useState<"request" | "reset" | "success">(
    token ? "reset" : "request"
  );
  const [error, setError] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [emailSent, setEmailSent] = useState<boolean>(false);

  // Request Reset Link Form
  const {
    register: registerRequest,
    handleSubmit: handleSubmitRequest,
    formState: { errors: errorsRequest },
  } = useForm<ForgotPasswordFormData>();

  // Reset Password Form
  const {
    register: registerReset,
    handleSubmit: handleSubmitReset,
    formState: { errors: errorsReset },
    watch,
  } = useForm<ResetPasswordFormData>({
    defaultValues: {
      token: token || "",
      email: email || "",
    },
  });

  const password = watch("password");

  // Request Reset Link
  const onRequestReset = async (data: ForgotPasswordFormData) => {
    try {
      setError("");
      setIsSubmitting(true);
      const response = await authApi.post("/forgot-password", {
        email: data.email,
      });

      if (response.data.success || response.data.status) {
        setEmailSent(true);
      } else {
        setError(response.data.message || "Failed to send reset link");
      }
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          "Failed to send reset link. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset Password
  const onResetPassword = async (data: ResetPasswordFormData) => {
    try {
      setError("");
      setIsSubmitting(true);

      const response = await authApi.post("/reset-password", {
        token: data.token,
        email: data.email,
        password: data.password,
        password_confirmation: data.password_confirmation,
      });

      if (response.data.success || response.data.status) {
        setStep("success");
      } else {
        setError(response.data.message || "Failed to reset password");
      }
    } catch (err: any) {
      setError(
        err.response?.data?.message || "Failed to reset password. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // If success, show success message
  if (step === "success") {
    return (
      <AuthLayout>
        <div className="cms-form-shell max-w-md mx-auto text-center">
          <div className="mb-6">
            <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="h-8 w-8 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h1 className="cms-form-section-title">
              Password Reset Successful
            </h1>
            <p className="cms-type-helper mb-6">
              Your password has been reset successfully. You can now login with your new password.
            </p>
            <Link
              to="/login"
              className="cms-btn-primary"
            >
              Back to Login
            </Link>
          </div>
        </div>
      </AuthLayout>
    );
  }

  // If email sent, show confirmation
  if (emailSent && !token) {
    return (
      <AuthLayout>
        <div className="cms-form-shell max-w-md mx-auto text-center">
          <div className="mb-6">
            <div className="h-16 w-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="h-8 w-8 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
            <h1 className="cms-form-section-title">
              Check Your Email
            </h1>
            <p className="cms-type-helper mb-6">
              We've sent a password reset link to your email address. Please check your inbox and follow the instructions.
            </p>
            <Link
              to="/login"
              className="cms-btn-primary"
            >
              Back to Login
            </Link>
          </div>
        </div>
      </AuthLayout>
    );
  }

  // Reset Password Form
  if (step === "reset" && token) {
    return (
      <AuthLayout>
        <div className="cms-form-shell w-full max-w-4xl mx-auto overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2">
            {/* Left Section - Logo and Branding */}
            <div className="bg-gray-50 p-8 md:p-12 flex flex-col items-center justify-center text-center">
              <div className="mb-6">
                <img 
                  src="/logo.png" 
                  alt="Logo" 
                  className="h-40 w-40 md:h-48 md:w-48 object-contain mx-auto"
                />
              </div>
              <h2 className="cms-form-section-title">Set New Password</h2>
              <p className="cms-type-helper">Choose a strong password for your account</p>
            </div>

            {/* Right Section - Reset Password Form */}
            <div className="p-8 md:p-12">
              <div className="mb-6">
                <h1 className="cms-form-section-title">
                  Reset Password
                </h1>
                <p className="cms-type-helper">
                  Enter your new password below
                </p>
              </div>

          {error && (
            <div className="cms-form-error-banner">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmitReset(onResetPassword)} className="space-y-5 cms-form-shell">
            <input type="hidden" {...registerReset("token")} />
            <input type="hidden" {...registerReset("email")} />

            <div>
              <label
                htmlFor="password"
                className="cms-type-form-label"
              >
                New Password
              </label>
              <input
                id="password"
                type="password"
                {...registerReset("password", {
                  required: "Password is required",
                  minLength: {
                    value: 8,
                    message: "Password must be at least 8 characters",
                  },
                })}
                className={`cms-form-control ${errorsReset.password ? "error" : ""} ${
                  errorsReset.password ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Enter your new password"
                disabled={isSubmitting}
              />
              {errorsReset.password && (
                <p className="cms-type-error mt-1">
                  {errorsReset.password.message}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="password_confirmation"
                className="cms-type-form-label"
              >
                Confirm Password
              </label>
              <input
                id="password_confirmation"
                type="password"
                {...registerReset("password_confirmation", {
                  required: "Please confirm your password",
                  validate: (value) =>
                    value === password || "Passwords do not match",
                })}
                className={`cms-form-control ${errorsReset.password_confirmation ? "error" : ""} ${
                  errorsReset.password_confirmation
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
                placeholder="Confirm your new password"
                disabled={isSubmitting}
              />
              {errorsReset.password_confirmation && (
                <p className="cms-type-error mt-1">
                  {errorsReset.password_confirmation.message}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="cms-btn-primary w-full"
            >
              {isSubmitting ? "Resetting..." : "Reset Password"}
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="cms-type-helper underline"
            >
              Back to Login
            </Link>
          </div>
            </div>
          </div>
        </div>
      </AuthLayout>
    );
  }

  // Request Reset Link Form
  return (
    <AuthLayout>
        <div className="cms-form-shell w-full max-w-4xl mx-auto overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-2">
          {/* Left Section - Logo and Branding */}
          <div className="bg-gray-50 p-8 md:p-12 flex flex-col items-center justify-center text-center">
            <div className="mb-6">
              <img 
                src="/logo.png" 
                alt="Logo" 
                className="h-40 w-40 md:h-48 md:w-48 object-contain mx-auto"
              />
            </div>
            <h2 className="cms-form-section-title">Reset Password</h2>
            <p className="cms-type-helper">We'll help you get back into your account</p>
          </div>

          {/* Right Section - Forgot Password Form */}
          <div className="p-8 md:p-12">
            <div className="mb-6">
              <h1 className="cms-form-section-title">
                Forgot Password
              </h1>
                <p className="cms-type-helper">
                  Enter your email address and we'll send you a link to reset your password.
                </p>
              </div>

        {error && (
          <div className="cms-form-error-banner">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmitRequest(onRequestReset)} className="space-y-5 cms-form-shell">
          <div>
            <label
              htmlFor="email"
              className="cms-type-form-label"
            >
              Email Address
            </label>
            <input
              id="email"
              type="email"
              {...registerRequest("email", {
                required: "Email is required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email address",
                },
              })}
              className={`cms-form-control ${errorsRequest.email ? "error" : ""} ${
                errorsRequest.email ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Enter your email address"
              disabled={isSubmitting}
            />
            {errorsRequest.email && (
              <p className="cms-type-error mt-1">
                {errorsRequest.email.message}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="cms-btn-primary w-full"
          >
            {isSubmitting ? "Sending..." : "Send Reset Link"}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/login"
            className="cms-type-helper underline"
          >
            Back to Login
          </Link>
        </div>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
};

export default ForgotPassword;


